import { motion } from "framer-motion";
import { ArrowRight, Flame } from "lucide-react";

const lines = [
  { text: "Build a $10k - $30k+/month", accent: true },
  { text: "life insurance business", accent: false },
];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.14, delayChildren: 0.25 } },
};
const lineAnim = {
  hidden: { y: "115%" },
  show: { y: "0%", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export default function Hero({ onStartQuiz }) {
  return (
    <section id="hero" data-testid="hero-section" className="relative overflow-hidden pt-2 pb-10 sm:pt-3 sm:pb-14">
      <div className="absolute inset-0 hero-grid-bg" aria-hidden="true" />
      <motion.div
        aria-hidden="true"
        className="absolute -top-40 left-1/2 h-[560px] w-[900px] -translate-x-1/2 rounded-full opacity-25 blur-3xl"
        style={{ background: "radial-gradient(closest-side, #00C6EF, transparent)" }}
        animate={{ scale: [1, 1.12, 1], opacity: [0.22, 0.3, 0.22] }}
        transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
      />
      <div className="relative max-w-5xl mx-auto px-5 text-center">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col items-center gap-1"
          data-testid="hero-eyebrow"
        >
          <img
            src="/bfg-logo.png"
            alt="Breeze Financial Group"
            className="h-44 sm:h-64 -mt-4 sm:-mt-8 w-auto object-contain drop-shadow-[0_0_28px_rgba(0,198,239,0.45)]"
          />
          <span className="-mt-8 sm:-mt-14 inline-flex items-center gap-2 rounded-full border border-sky-400/30 bg-sky-400/10 px-4 py-1.5 text-xs font-semibold tracking-[0.18em] uppercase text-sky-300">
            <Flame className="h-3.5 w-3.5" />
            Now Hiring · Licensed & Unlicensed
          </span>
        </motion.div>

        <motion.h1
          variants={container}
          initial="hidden"
          animate="show"
          className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08]"
          data-testid="hero-headline"
        >
          {lines.map((l, i) => (
            <span key={i} className="mask-line">
              <motion.span variants={lineAnim} className={l.accent ? "hero-title-gradient" : "text-white"}>
                {l.text}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-5 text-base sm:text-xl font-semibold tracking-tight text-slate-100 max-w-2xl mx-auto leading-snug"
          data-testid="hero-description"
        >
          See how new agents are getting licensed, plugged into warm leads, and{" "}
          <strong className="hero-title-gradient font-extrabold">closing within their <span className="underline decoration-[#00C6EF] decoration-[3px] underline-offset-4">first 30 days</span></strong>
        </motion.p>

        <motion.ul
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.78, duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 sm:mt-12 grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-w-2xl mx-auto text-left"
          data-testid="hero-bullets"
        >
          {[
            "No experience? No problem.",
            "Get licensed in 2-3 weeks",
            "Proven training & true mentorship",
            "Many new agents hit $10k+ months within 90 days",
          ].map((b) => (
            <li key={b} className="glass-card rounded-2xl px-4 py-3 flex items-center gap-3 text-sm font-semibold text-slate-100">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="shrink-0">
                <path d="M12 2l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.2 5.9 20.6l1.4-6.8L2.2 9.1l6.9-.8L12 2z" fill="#00C6EF"/>
              </svg>
              {b}
            </li>
          ))}
        </motion.ul>

        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8"
        >
          <p className="text-xl sm:text-2xl font-bold text-white" data-testid="hero-cta-prompt">
            Ready to take your income to new heights?
          </p>
          <div className="mt-5 flex items-center justify-center">
            <button
              onClick={onStartQuiz}
              data-testid="hero-cta-get-to-work"
              className="btn-primary-glow pulse-ring group relative w-full sm:w-auto rounded-full px-12 sm:px-16 py-5 sm:py-6 text-lg sm:text-xl font-extrabold text-white inline-flex items-center justify-center gap-3 overflow-hidden"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" aria-hidden="true" />
              <span className="relative">Let's Get To Work</span>
              <ArrowRight className="relative h-6 w-6 group-hover:translate-x-1.5 transition-transform duration-300" />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
